import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, doc, getDoc, setDoc, updateDoc, collection, addDoc, serverTimestamp } from '../firebase/config';
import { useAuth } from './AuthContext';

const CartContext = createContext();

// ---------- Recursively strip undefined values ----------
// Firestore rejects any field set to `undefined`.
const cleanForFirestore = (obj) => {
  if (Array.isArray(obj)) return obj.map(cleanForFirestore);
  if (obj && typeof obj === 'object' && obj.constructor === Object) {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined) out[k] = cleanForFirestore(v);
    });
    return out;
  }
  return obj;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cartTotal, setCartTotal] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartInitialTab, setCartInitialTab] = useState('products');

  // Load cart from Firebase
  useEffect(() => {
    if (user) {
      loadCart();
    } else {
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch {
          setCartItems([]);
        }
      } else {
        setCartItems([]);
      }
    }
  }, [user]);

  // Update totals whenever cart changes
  useEffect(() => {
    const total = cartItems.reduce(
      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
      0
    );
    const count = cartItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );
    setCartTotal(total);
    setCartCount(count);
  }, [cartItems]);

  const loadCart = async () => {
    setLoading(true);
    try {
      const cartDoc = await getDoc(doc(db, 'carts', user.uid));
      if (cartDoc.exists()) {
        setCartItems(cartDoc.data().items || []);
      } else {
        await setDoc(doc(db, 'carts', user.uid), {
          items: [],
          createdAt: serverTimestamp(),
        });
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCart = async (items) => {
    if (user) {
      try {
        await updateDoc(doc(db, 'carts', user.uid), {
          items: cleanForFirestore(items),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        console.error('Error saving cart:', error);
      }
    } else {
      try {
        localStorage.setItem('guestCart', JSON.stringify(items));
      } catch (error) {
        console.error('Error saving guest cart:', error);
      }
    }
  };

  const addToCart = async (product, quantity = 1, options = {}) => {
    try {
      const existingItemIndex = cartItems.findIndex(
        item =>
          item.id === product.id &&
          JSON.stringify(item.options || {}) === JSON.stringify(options || {})
      );

      let newItems;
      if (existingItemIndex !== -1) {
        newItems = cartItems.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: (Number(item.quantity) || 0) + quantity }
            : item
        );
      } else {
        // Only keep the fields we actually want — avoids storing undefined
        const newItem = {
          id: product.id,
          name: product.name || 'Product',
          brand: product.brand || null,
          price: Number(product.price) || 0,
          image: product.image || null,
          category: product.category || null,
          professionalId: product.professionalId || null,
          professionalName: product.professionalName || null,
          media: Array.isArray(product.media) ? product.media : [],
          mediaLegacy: product.mediaLegacy || null,
          quantity,
          options: options || {},
          addedAt: new Date().toISOString(),
        };
        newItems = [...cartItems, newItem];
      }

      setCartItems(newItems);
      await saveCart(newItems);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  const removeFromCart = async (productId, options = {}) => {
    try {
      const newItems = cartItems.filter(
        item =>
          !(
            item.id === productId &&
            JSON.stringify(item.options || {}) === JSON.stringify(options || {})
          )
      );
      setCartItems(newItems);
      await saveCart(newItems);
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  };

  const updateQuantity = async (productId, quantity, options = {}) => {
    try {
      if (quantity <= 0) {
        return await removeFromCart(productId, options);
      }

      const newItems = cartItems.map(item =>
        item.id === productId &&
        JSON.stringify(item.options || {}) === JSON.stringify(options || {})
          ? { ...item, quantity }
          : item
      );
      setCartItems(newItems);
      await saveCart(newItems);
      return true;
    } catch (error) {
      console.error('Error updating quantity:', error);
      return false;
    }
  };

  const clearCart = async () => {
    try {
      setCartItems([]);
      await saveCart([]);
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  const getCartTotal = () => cartTotal;
  const getCartCount = () => cartCount;

  const value = {
    cartItems,
    cartInitialTab,
    loading,
    cartTotal,
    cartCount,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    openCart: (tab) => {
      if (tab) setCartInitialTab(tab);
      setIsCartOpen(true);
    },
    closeCart: () => setIsCartOpen(false),
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};